import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Card,
  CardHeader,
  LoadingSpinner,
  TimelineEvent,
} from '@ais-ui/ui-core';
import { useGetShipByImo, useShipPortCallsHistory } from '@ais-ui/api';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import { TimelineContainer } from '../../../../components/TimelineContainer';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { useAuth } from '@ais-ui/auth';

// Define inspection interface to fix the type errors
interface Inspection {
  inspection_date: string;
  inspection_type: string;
  inspection_port: string;
  inspection_country: string;
  deficiency_count: number;
  deficiency_ground_for_detention_count: number;
}

interface Ban {
  ban_begin_date: string;
  ban_country_of_inspection: string;
  ban_end_date: string | null;
  ban_reason: string;
  related_psc: string;
  ban_status: string;
}

interface Sanction {
  sanction_by: string;
  program: string;
  reason: string;
  sanction_type: string;
  sanction_begin_date: string;
  sanction_end_date: string | null;
}

// Define extended vessel info interface
interface AdvancedIntegrity {
  inspections: Inspection[];
  bans: Ban[];
  sanctions: Sanction[];
}

interface ExtendedVesselInfo {
  advancedIntegrity?: AdvancedIntegrity;
}

interface TargetTimelineProps {
  imo: number;
}
interface ErrorResponse {
  response?: {
    parsedBody?: {
      code?: number;
    };
  };
}
export const TargetTimeline = ({ imo }: TargetTimelineProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const permissions = usePermissions();

  // Only fetch extended vessel info if user has integrity permissions
  const {
    data: extendedVesselInfo,
    isLoading: isLoadingExtendedVesselInfo,
    isError: isErrorExtendedVesselInfo,
  } = useGetShipByImo(imo, permissions.mdmIntegrity);

  const {
    data: rawPortCalls,
    isLoading: isLoadingPortCalls,
    error: portCallError,
  } = useShipPortCallsHistory(imo ? { imo: imo } : undefined);

  const EVENT_TYPES = useMemo(
    () => [
      {
        id: 'port-call',
        name: 'Port Call',
        color: '#3B82F6',
        disabled: false,
      },
      {
        id: 'inspection',
        name: 'Inspection',
        color: '#ff6e00',
        disabled: !permissions.mdmIntegrity,
      },
      {
        id: 'ban',
        name: 'Ban',
        color: '#311B92',
        disabled: !permissions.mdmIntegrity,
      },
      {
        id: 'sanction',
        name: 'Sanction',
        color: '#880E4F',
        disabled: !permissions.mdmIntegrity,
      },
    ],
    [permissions.mdmIntegrity]
  );

  // Get the port call event type
  const portCallType = EVENT_TYPES.find((type) => type.id === 'port-call')!;

  // Get the inspection event type
  const inspectionType = EVENT_TYPES.find((type) => type.id === 'inspection')!;

  const banType = EVENT_TYPES.find((type) => type.id === 'ban')!;

  const sanctionType = EVENT_TYPES.find((type) => type.id === 'sanction')!;

  // Port calls as timeline events
  const portCallEvents: TimelineEvent[] = useMemo(() => {
    if (!rawPortCalls || !rawPortCalls.records) {
      return [];
    }

    return rawPortCalls.records.map((historicalPortCall, i) => {
      // Create a constant for the port name with fallback
      const portName = historicalPortCall?.port?.name || 'Unknown';

      // Format the duration into a human-readable string
      const formatDuration = (totalSeconds: number) => {
        if (!totalSeconds) return 'unknown duration';

        const days = Math.floor(totalSeconds / (24 * 3600));
        const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        const parts = [];
        if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0)
          parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(', ') : 'less than a minute';
      };

      let stringDuration = 'unknown';
      // Calculate the duration between ata and atd
      if (historicalPortCall.ata && historicalPortCall.atd) {
        const durationInSeconds: number =
          (historicalPortCall.atd.getTime() -
            historicalPortCall.ata.getTime()) /
          1000;

        stringDuration = formatDuration(durationInSeconds);
      }

      const arrivalString = formatDateTimeWithTimeZone(historicalPortCall.ata);
      const departureString = formatDateTimeWithTimeZone(
        historicalPortCall.atd
      );

      return {
        id: `port-call-${i}`,
        date: historicalPortCall.ata as Date,
        title: portName,
        description: `Arrived at ${portName} at ${formatDateTimeWithTimeZone(
          historicalPortCall.ata
        )} and departed at ${formatDateTimeWithTimeZone(
          historicalPortCall.atd
        )} for a total of ${stringDuration}.`,
        type: portCallType,
        metadata: {
          Arrival: arrivalString,
          Departure: departureString,
          Duration: stringDuration,
        },
      };
    });
  }, [rawPortCalls, formatDateTimeWithTimeZone, portCallType]);

  // Inspection timeline events - only process if user has integrity permissions
  const inspectionEvents: TimelineEvent[] = useMemo(() => {
    if (
      !permissions.mdmIntegrity ||
      !extendedVesselInfo?.advancedIntegrity?.inspections
    ) {
      return [];
    }

    // Calculate date 90 days ago from today
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    // Filter inspections to only include those from the last 90 days
    return extendedVesselInfo.advancedIntegrity.inspections
      .filter((inspection: Inspection) => {
        const inspectionDate = new Date(inspection.inspection_date);
        return inspectionDate >= ninetyDaysAgo;
      })
      .map((inspection: Inspection) => {
        // Format the inspection type for display
        const formattedType =
          inspection.inspection_type
            ?.split('_')
            .map((word: string) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ') || 'Inspection';

        // Create a description based on the data
        let description = `${formattedType} conducted at ${inspection.inspection_port}, ${inspection.inspection_country}.`;

        if (inspection.deficiency_count > 0) {
          description += ` Found ${inspection.deficiency_count} deficiencies.`;
          if (inspection.deficiency_ground_for_detention_count > 0) {
            description += ` ${inspection.deficiency_ground_for_detention_count} were grounds for detention.`;
          }
        } else {
          description += ' No deficiencies were found.';
        }

        return {
          id: `inspection-${uuidv4()}`,
          date: new Date(inspection.inspection_date),
          title: `${formattedType} - ${inspection.inspection_port}`,
          description,
          type: inspectionType,
        };
      });
  }, [extendedVesselInfo, inspectionType, permissions.mdmIntegrity]);

  // Ban timeline events - only process if user has integrity permissions
  const banEvents: TimelineEvent[] = useMemo(() => {
    if (
      !permissions.mdmIntegrity ||
      !extendedVesselInfo?.advancedIntegrity?.bans
    ) {
      return [];
    }

    // Calculate date 90 days ago from today
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    // Filter bans to only include those from the last 90 days
    return extendedVesselInfo.advancedIntegrity.bans
      .filter((ban: Ban) => {
        const banDate = new Date(ban.ban_begin_date);
        return banDate >= ninetyDaysAgo;
      })
      .map((ban: Ban) => {
        // Create a description based on the data
        let description = `Ban issued by ${ban.ban_country_of_inspection} for ${ban.ban_reason}.`;

        // Add information about the ban status
        if (ban.ban_end_date) {
          description += ` The ban ended on ${formatDateTimeWithTimeZone(
            new Date(ban.ban_end_date)
          )}.`;
        } else {
          description += ` The ban is currently ${ban.ban_status.toLowerCase()}.`;
        }

        if (ban.related_psc) {
          description += ` Related to PSC reference: ${ban.related_psc}.`;
        }

        return {
          id: `ban-${uuidv4()}`,
          date: new Date(ban.ban_begin_date),
          title: `Ban - ${ban.ban_country_of_inspection}`,
          description,
          type: banType,
          metadata: {
            'Ban Country': ban.ban_country_of_inspection,
            'Begin Date': formatDateTimeWithTimeZone(
              new Date(ban.ban_begin_date)
            ),
            'End Date': ban.ban_end_date
              ? formatDateTimeWithTimeZone(new Date(ban.ban_end_date))
              : 'Ongoing',
            Status: ban.ban_status,
            Reason: ban.ban_reason,
          },
        };
      });
  }, [
    extendedVesselInfo,
    banType,
    permissions.mdmIntegrity,
    formatDateTimeWithTimeZone,
  ]);

  // Sanction timeline events - only process if user has integrity permissions
  const sanctionEvents: TimelineEvent[] = useMemo(() => {
    if (
      !permissions.mdmIntegrity ||
      !extendedVesselInfo?.advancedIntegrity?.sanctions
    ) {
      return [];
    }

    // Calculate date 90 days ago from today
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    // Filter sanctions to only include those from the last 90 days
    return extendedVesselInfo.advancedIntegrity.sanctions
      .filter((sanction: Sanction) => {
        const sanctionDate = new Date(sanction.sanction_begin_date);
        return sanctionDate >= ninetyDaysAgo;
      })
      .map((sanction: Sanction) => {
        // Create a description based on the data
        let description = `${sanction.sanction_type} sanction imposed by ${sanction.sanction_by} under ${sanction.program} program.`;

        if (sanction.reason) {
          description += ` Reason: ${sanction.reason}.`;
        }

        if (sanction.sanction_end_date) {
          description += ` The sanction ended on ${formatDateTimeWithTimeZone(
            new Date(sanction.sanction_end_date)
          )}.`;
        } else {
          description += ` The sanction is currently active.`;
        }

        return {
          id: `sanction-${uuidv4()}`,
          date: new Date(sanction.sanction_begin_date),
          title: `Sanction - ${sanction.sanction_by}`,
          description,
          type: sanctionType,
          metadata: {
            'Imposed By': sanction.sanction_by,
            Program: sanction.program,
            Type: sanction.sanction_type,
            'Begin Date': formatDateTimeWithTimeZone(
              new Date(sanction.sanction_begin_date)
            ),
            'End Date': sanction.sanction_end_date
              ? formatDateTimeWithTimeZone(new Date(sanction.sanction_end_date))
              : 'Ongoing',
            Reason: sanction.reason || 'Not specified',
          },
        };
      });
  }, [
    extendedVesselInfo,
    sanctionType,
    permissions.mdmIntegrity,
    formatDateTimeWithTimeZone,
  ]);

  // Combine port calls and inspections
  const allEvents = useMemo(() => {
    return [
      ...portCallEvents,
      ...inspectionEvents,
      ...banEvents,
      ...sanctionEvents,
    ];
  }, [portCallEvents, inspectionEvents, banEvents, sanctionEvents]);

  // Separate loading and error state handling based on permissions
  const isLoading = useMemo(() => {
    if (permissions.mdmIntegrity) {
      return isLoadingExtendedVesselInfo || isLoadingPortCalls;
    }
    return isLoadingPortCalls;
  }, [
    isLoadingExtendedVesselInfo,
    isLoadingPortCalls,
    permissions.mdmIntegrity,
  ]);

  const [portCallsNotFound, setPortCallsNotFound] = useState(false);
  const [serviceNotAvailable, setServiceNotAvailable] = useState(false);

  const isErrorPortCalls = useMemo(() => {
    if (!portCallError) {
      return false;
    }

    const errorCode: undefined | number = (portCallError as ErrorResponse)
      ?.response?.parsedBody?.code;

    // 422 means that we couldn't find the ship in the database
    if (errorCode === 422) {
      setPortCallsNotFound(true);
      return false;
    }

    // 404 means the service isn't available
    if (errorCode === 404) {
      setServiceNotAvailable(true);
      return false;
    }

    return true;
  }, [portCallError]);

  const isError = useMemo(() => {
    if (permissions.mdmIntegrity) {
      return isErrorExtendedVesselInfo || isErrorPortCalls;
    }
    return isErrorPortCalls;
  }, [isErrorExtendedVesselInfo, isErrorPortCalls, permissions.mdmIntegrity]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="min-h-[112px]">
          <div className="flex space-x-2 items-center justify-center h-full">
            <LoadingSpinner></LoadingSpinner>
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (serviceNotAvailable) {
    return (
      <Card>
        <CardHeader className="h-[112px]">
          <div className="flex space-x-2 items-center justify-center h-full">
            <p className="text-lg">Port call service is not available</p>
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (portCallsNotFound) {
    return (
      <Card>
        <CardHeader className="h-[112px]">
          <div className="flex space-x-2 items-center justify-center h-full">
            <p className="text-lg">Couldn't find port call data for vessel</p>
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader className="h-[112px]">
          <div className="flex space-x-2 items-center justify-center h-full">
            <p className="text-destructive text-lg">
              Error loading timeline data
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <TimelineContainer
      events={allEvents}
      eventTypes={EVENT_TYPES}
      className="mb-12 pl-6 pr-10"
      title="Event timeline"
      durationDays={90}
    />
  );
};
